<template>
  <v-dialog
    v-model="localDialog"
    width="40%"
    persistent
    :open-delay="0"
    :close-delay="0"
  >
    <v-card>
      <v-card-title class="text-h6">
        <span>{{ isEntryMode ? "Assign Shift" : "Update Shift Assignment" }}</span>
      </v-card-title>
      <v-divider></v-divider>

      <v-card-text>
        <v-form ref="employeeShiftMappingForm" @submit.prevent="submitForm">
          <v-row>
            <v-col cols="12">
              <v-autocomplete
                v-model="employee_id.value.value"
                :items="employeeList"
                :item-title="employeeLabel"
                item-value="id"
                :error-messages="employee_id.errorMessage.value"
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                clearable
              
                  :menu-props="{ contentClass: 'my-custom-dropdown' }"
>
                <template #label>
                  Employee
                  <span class="text-red-accent-4">*</span>
                </template>
              </v-autocomplete>

              <v-autocomplete
                v-model="shift_id.value.value"
                :items="shiftList"
                item-value="id"
                :item-title="formatShiftOption"
                :error-messages="shift_id.errorMessage.value"
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                clearable
              
                  :menu-props="{ contentClass: 'my-custom-dropdown' }"
>
                <template #label>
                  Shift
                  <span class="text-red-accent-4">*</span>
                </template>
              </v-autocomplete>

              <v-date-input
                input-format="dd/mm/yyyy"
                label="Start Date"
                density="compact"
                prepend-icon=""
                prepend-inner-icon="mdi-calendar"
                variant="outlined"
                bg-color="grey-lighten-4"
                v-model="start_date.value.value"
                :error-messages="start_date.errorMessage.value"
                autocomplete="off"
                clearable
              ></v-date-input>

              <v-date-input
                input-format="dd/mm/yyyy"
                label="End Date"
                density="compact"
                prepend-icon=""
                prepend-inner-icon="mdi-calendar"
                variant="outlined"
                bg-color="grey-lighten-4"
                v-model="end_date.value.value"
                :error-messages="end_date.errorMessage.value"
                autocomplete="off"
                clearable
              ></v-date-input>

              <v-textarea
                v-model="remarks.value.value"
                :error-messages="remarks.errorMessage.value"
                label="Remarks"
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                rows="2"
                clearable
              ></v-textarea>
              <input type="hidden" v-model="employee_shift_id.value.value" />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions>
        <v-layout class="justify-end">
          <v-btn class="bg-red" @click="closeDialog">Close</v-btn>
          <v-btn
            class="bg-brand w-15 ms-2"
            variant="flat"
            type="submit"
            :loading="loading"
            @click="submitForm"
          >
            {{ isEntryMode ? "Save" : "Update" }}
          </v-btn>
        </v-layout>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref } from "vue";
import { useStore } from "vuex";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { toast } from "vue3-toastify";
import formattedDate from "@/plugins/dateFormatter";

export default {
  name: "EmployeeShiftMappingSetup",
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Object,
      default: () => ({
        employee_shift_id: "",
        employee_id: "",
        shift_id: "",
        start_date: "",
        end_date: "",
        remarks: "",
      }),
    },
    employeeList: {
      type: Array,
      default: () => [],
    },
    shiftList: {
      type: Array,
      default: () => [],
    },
    selectedEmployee: {
      type: [String, Number, null],
      default: null,
    },
  },
  data() {
    return {
      localDialog: this.dialog,
    };
  },
  computed: {
    isEntryMode() {
      return !this.employee_shift_id.value.value;
    },
  },
  watch: {
    dialog(val) {
      this.localDialog = val;
      if (val && this.selectedEmployee && !this.data.employee_id) {
        this.employee_id.setValue(this.selectedEmployee, false);
      }
    },
    localDialog(val) {
      this.$emit("update:dialog", val);
      if (!val) {
        this.resetForm();
      }
    },
    data: {
      handler(newVal) {
        this.populateForm(newVal || {});
      },
      immediate: true,
      deep: true,
    },
    selectedEmployee(val) {
      if (val && !this.data.employee_id) {
        this.employee_id.setValue(val, false);
      }
    },
  },
  methods: {
    employeeLabel(item) {
      if (!item) return "";
      return item.code ? `${item.code} - ${item.name}` : item.name;
    },
    closeDialog() {
      this.localDialog = false;
    },
    populateForm(payload) {
      const values = {
        employee_shift_id: payload.employee_shift_id || payload.id || "",
        employee_id: payload.employee_id ?? "",
        shift_id: payload.shift_id ?? "",
        start_date: payload.start_date,
        end_date: payload.end_date,
        remarks: payload.remarks || "",
      };
      this.setValues(values, false);
    },
    submitForm() {
      this.submitHandler();
    },
    formatTime(value) {
      if (!value) return "";
      const timeString = String(value);
      if (timeString.length >= 5) {
        return timeString.slice(0, 5);
      }
      return timeString;
    },
    formatShiftOption(item) {
      if (!item) return "";
      const shiftName = item.shift_name || "Unnamed Shift";
      const inTime = this.formatTime(item.in_time);
      const outTime = this.formatTime(item.out_time);
      if (!inTime && !outTime) {
        return shiftName;
      }
      return `${shiftName} (${inTime || "--:--"} - ${outTime || "--:--"})`;
    },
  },
  setup(props, { emit }) {
    const store = useStore();
    const loading = ref(false);

    const { handleSubmit, resetForm, setValues } = useForm({
      validationSchema: yup.object({
        employee_id: yup.mixed().required().label("Employee"),
        shift_id: yup.mixed().required().label("Shift"),
        start_date: yup.string().required().label("Start Date"),
        end_date: yup
          .string()
          .nullable()
          .test("is-after-start", "End Date cannot be before Start Date", function (value) {
            const { start_date } = this.parent;
            if (!value || !start_date) return true;
            return new Date(value) >= new Date(start_date);
          })
          .label("End Date"),
        remarks: yup.string().nullable().max(255).label("Remarks"),
        employee_shift_id: yup.string().nullable(),
      }),
      initialValues: {
        employee_shift_id: "",
        employee_id: "",
        shift_id: "",
        start_date: "",
        end_date: "",
        remarks: "",
      },
    });

    const employee_shift_id = useField("employee_shift_id");
    const employee_id = useField("employee_id");
    const shift_id = useField("shift_id");
    const start_date = useField("start_date");
    const end_date = useField("end_date");
    const remarks = useField("remarks");

    const submitHandler = handleSubmit((values) => {
      loading.value = true;
      values.start_date = formattedDate(start_date.value.value);
      values.end_date = formattedDate(end_date.value.value);
      
      store
        .dispatch("saveEmployeeShiftMapping", values)
        .then((response) => {
          toast.success(response?.data?.message || "Shift assigned successfully.");
          resetForm();
          emit("saved");
        })
        .catch((error) => {
          const message = error?.response?.data?.message || "Failed to save shift mapping.";
          toast.error(message);
        })
        .finally(() => {
          loading.value = false;
        });
    });

    return {
      loading,
      employee_shift_id,
      employee_id,
      shift_id,
      start_date,
      end_date,
      remarks,
      submitHandler,
      resetForm,
      setValues,
    };
  },
};
</script>

